import { compactNumber } from "@/lib/formatNumber";
import { postShareLocked } from "@/lib/privacy";
import { renderRichText } from "@/components/RichText";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { fxStyle } from "@/lib/mediaFx";
import { useUserBlocked, withoutAuthor } from "@/lib/blocks";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { isOffline } from "@/lib/netStatus";
import { enqueueOffline, shouldQueue, useQueuedOffline } from "@/lib/offlineQueue";
import { requireLogin } from "@/lib/guestGate";
import { useApp } from "@/context/AppContext";
import { resolveMediaUrl } from "@/components/FileUpload";
import { CommentModal } from "@/components/CommentModal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Heart, MessageCircle, Share2, Film, Eye, Megaphone, ChevronRight, ChevronUp, ChevronDown, Globe, MoreVertical, Trash2, Flag, Play, Pause, RotateCcw, RotateCw, ArrowLeft, Captions, Loader2, Music, Bookmark, Link2, EyeOff, UserX, Ban, Camera, WifiOff } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { sharePostCard } from "@/lib/nativeShare";
import { toShareLink } from "@/lib/share";
import { publicOrigin } from "@/lib/publicOrigin";
import { ReportDialog } from "@/components/ReportDialog";
import { BlockUserDialog } from "@/components/BlockUserDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody } from "@/components/ui/dialog";
import { AudienceOptions } from "@/components/AudienceOptions";
import { audienceOf } from "@/lib/postAudience";
import { ctaLabel, performAdCta } from "@/lib/adCta";
import { reelCovered, usePageHidden } from "@/lib/reelCover";
import { AdLeadDialog } from "@/components/AdLeadDialog";
import { adEvery as adEveryFor } from "@/lib/adFreq";
import { reportAdmobSkippedByYalola, insertGoogleReelSlides, reelsAdMode, preloadNativeAd } from "@/lib/admob";
import { GoogleReelSlide } from "@/components/AdmobNative";
import { toast } from "sonner";
import { isHlsSrc, derivePosterFromHls, attachHls, preconnectVideoHost, nativeStartHeight, lastPlayerUsed, isSlowNetwork } from "@/lib/videoSource";
import { dataSaverActive } from "@/lib/mediaUrl";
import { warmHlsOnce } from "@/lib/reelsWarmup";
import { attachAdVideoTracking } from "@/lib/adVideoTrack";
import { NEUTRAL_POSTER, onPosterError } from "@/lib/neutralPoster";
import { prefetchReel, getReelBlob, dropReelBlob, setReelCacheLimit, releaseReelCache } from "@/lib/reelVideoCache";
import { prefetchedGet } from "@/lib/bootPrefetch";
import { markFeedReady } from "@/lib/lowPriority";
import { readMutedPref, writeMutedPref, getReelProgress, saveReelProgress, readLastReel, saveLastReel, readMyLangOnly, writeMyLangOnly } from "@/lib/reelPrefs";
import { useVolumeKeyUnmute, VOLUME_PRIORITY } from "@/lib/volumeKeys";
import { setReelHandoff } from "@/lib/reelHandoff";
import { recordReelStart } from "@/lib/reelMetrics";
import { SoundControl } from "@/components/SoundControl";
import { createMusicFader } from "@/lib/audioFade";
import { avatarSrc } from "@/lib/defaults";
import { useIsMobile } from "@/lib/useIsMobile";
import { isNativeApp } from "@/lib/platform";
import { useLeaveGuard } from "@/lib/useLeaveGuard";
import { useSeo, excerpt, PUBLIC_ORIGIN } from "@/lib/seo";
import { stripRichMarkers } from "@/lib/richFormat";
// Lot 593 — reels dont le 1er segment HLS est mis en cache HTTP au-delà des voisins branchés (+2/+3).
const REELS_WARM_AHEAD = 2;
// Lot 635 — pagination : taille de page serveur (GET /reels limit) et marge (en cartes) avant la fin pour demander la suivante.
const REELS_PAGE = 20;
const REELS_LOAD_AHEAD = 6;

// Cloudflare Stream + Bunny serve adaptive HLS (.m3u8) — played via hls.js for
// instant, TikTok-style streaming (see @/lib/videoSource, the ONE source of
// truth for HLS detection / poster / attach). Plain R2 uploads use direct src.
const hlsPoster = (u) => derivePosterFromHls(resolveMediaUrl(u));
// One Bunny segment (4 s): what a non-active neighbour reel is allowed to buffer.
const NEIGHBOUR_BUFFER_S = 4;
// Voisin qui REPREND en cours de vidéo : 2 segments (8 s) au lieu d'un, sinon une reprise en
// fin de segment ne laisse que ~0,5 s tamponnée devant la tête → micro-gel juste après le départ.
const NEIGHBOUR_RESUME_BUFFER_S = 8;
const RESUME_REWIND_S = 2;
const resumeAt = (t) => (t > 3 ? Math.max(0, t - RESUME_REWIND_S) : 0);
// Cartes complètes montées autour du reel actif (au-delà : case vide de même hauteur). Couvre near (≤ 5) + keep (≤ 3).
const REEL_WINDOW = 6;
// App native : nombre maximal de lecteurs vidéo (hls.js / <video> natif) vivants en même temps —
// actif + 1 devant + 1 derrière. Chaque lecteur = tampon MSE + décodeur matériel (4-8 sur Android).
const NATIVE_LIVE_PLAYERS = 3;

const ReelSkeleton = () => (
  <div className="reel-card" style={{ height: "100dvh" }} data-testid="reels-skeleton">
    <div className="absolute inset-0 yalola-skeleton" />
    <div className="reel-actions absolute right-3 z-10 flex flex-col items-center gap-5">
      {[0, 1, 2, 3].map((k) => (
        <div key={k} className="flex flex-col items-center gap-1">
          <div className="h-11 w-11 rounded-full yalola-skeleton" style={{ background: "rgba(255,255,255,0.16)" }} />
          <div className="h-2 w-6 rounded yalola-skeleton" style={{ background: "rgba(255,255,255,0.16)" }} />
        </div>
      ))}
    </div>
    <div className="reel-caption absolute left-4 right-20 z-10">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-full yalola-skeleton" style={{ background: "rgba(255,255,255,0.18)" }} />
        <div className="h-3 w-28 rounded yalola-skeleton" style={{ background: "rgba(255,255,255,0.18)" }} />
      </div>
      <div className="mt-3 h-3 w-3/4 rounded yalola-skeleton" style={{ background: "rgba(255,255,255,0.12)" }} />
      <div className="mt-2 h-3 w-1/2 rounded yalola-skeleton" style={{ background: "rgba(255,255,255,0.12)" }} />
    </div>
  </div>
);

const ReelCard = React.memo(function ReelCard({ reel, active, near, keep = false, covered = false, muted, countView, onToggleMute, onDeleted, onAutoplayBlocked, onCommentsChange }) {
  const { user, t, config } = useApp();
  const resumeEnabled = config?.settings?.reels_resume_enabled !== false;
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const viewedRef = useRef(false);
  const [liked, setLiked] = useState(reel.my_reaction === "love");
  const [likes, setLikes] = useState(reel.reactions?.love || 0);
  const [views, setViews] = useState(reel.views_count || 0);
  const [commentCount, setCommentCount] = useState(reel.comment_count || 0);
  const [shares, setShares] = useState(reel.shares_count || 0);
  const [showComments, setShowComments] = useState(false);
  // TikTok-style comments: the video shrinks to the top band (keeps playing — single audio
  // source), the comments sheet takes the rest. Overlays are hidden meanwhile.
  const overlays = !showComments;
  useEffect(() => { onCommentsChange?.(showComments); }, [showComments, onCommentsChange]);
  useEffect(() => { if (!active && showComments) setShowComments(false); }, [active, showComments]);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [progress, setProgress] = useState(0);
  const [heartBurst, setHeartBurst] = useState(false);
  const [captionOpen, setCaptionOpen] = useState(false);
  const [captions, setCaptions] = useState(reel.captions || []);
  const [ccOn, setCcOn] = useState(true);
  const [genLoading, setGenLoading] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [saved, setSaved] = useState(!!reel.is_saved);
  const [blockOpen, setBlockOpen] = useState(false);
  // Lot 653 — audience du Reel (même choix que les publications), modifiable par l'auteur depuis le menu ⋯.
  const [audience, setAudience] = useState(audienceOf(reel.visibility).key);
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [audienceBusy, setAudienceBusy] = useState(false);
  const changeAudience = async (v) => {
    if (v === audience) { setAudienceOpen(false); return; }
    setAudienceBusy(true);
    try {
      await api.patch(`/posts/${reel.id}`, { visibility: v });
      setAudience(v); reel.visibility = v;
      toast.success(t("audience.changed", "Audience modifiée : {label}").replace("{label}", t(audienceOf(v).tkey, audienceOf(v).label)));
      setAudienceOpen(false);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setAudienceBusy(false); }
  };
  // Facebook-style player controls (tap = play/pause, scrubber, ±10s, time).
  const [paused, setPaused] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [duration, setDuration] = useState(0);
  const [curTime, setCurTime] = useState(0);
  const scrubbing = useRef(false);
  const controlsTimer = useRef(null);
  const tapRef = useRef({ last: 0, timer: null });
  // Resume-playback: seek back to where the member left off (persisted per reel).
  const resumeTargetRef = useRef(0);
  const resumedRef = useRef(false);
  const lastSaveRef = useRef(0);
  const lastPctRef = useRef(-1);
  const lastSecRef = useRef(-1);
  // Position de reprise connue DÈS le montage : le préchargement (voisin) télécharge directement
  // le bon segment HLS / la bonne plage MP4 → l'activation est instantanée, sans « seek » qui gelait.
  // On reprend 2 s AVANT la position (contexte + départ sur du contenu déjà tamponné).
  const [startAt] = useState(() => resumeAt(resumeEnabled ? getReelProgress(reel.id) : 0));
  // Startup-speed metric: activation → first 'playing' frame, sent as aggregates.
  const startT0Ref = useRef(0);
  const startDoneRef = useRef(true);
  // Lot 555 : le reel avait-il déjà des données décodables (readyState ≥ 2) à l'arrivée ? → « préchargé ».
  const startPreRef = useRef(0);
  // First-frame fade (TikTok feel): a full-screen poster overlay covers the video
  // and fades out smoothly on the first 'playing' event, so the eye never sees a
  // black "gap" while the decoder spins up. Reset when the reel leaves the screen.
  const [posterFaded, setPosterFaded] = useState(false);
  // Poster URL that fails to load (404 / Cloudflare 400) → behave as if there were no
  // poster at all: dark skeleton, never the browser's broken-image « ? » glyph.
  const [posterBroken, setPosterBroken] = useState(false);
  const videoSrc = reel.hls_url || reel.video_url;
  const isHls = isHlsSrc(videoSrc);
  // Background music (chosen in the composer) plays over the reel exactly like on
  // feed posts: the video is muted (music carries the sound) unless the author kept
  // the video's own sound. Respects the chosen volume + start offset.
  const hasMusic = !!reel.music_url;
  const keepMediaSound = !!reel.music_keep_media_sound;
  const musicVolume = typeof reel.music_volume === "number" ? reel.music_volume : 1;
  const musicStart = reel.music_start || 0;
  const videoElMuted = hasMusic && !keepMediaSound ? true : muted;
  const [musicPlaying, setMusicPlaying] = useState(false);
  const directUrl = isHls ? undefined : resolveMediaUrl(videoSrc);
  // MP4 direct (pas encore en mémoire) : fragment média « #t=… » → le navigateur commence sa
  // requête à la position de reprise au lieu de charger le début puis sauter.
  const directSrc = directUrl && startAt > 0 ? `${directUrl}#t=${Math.floor(startAt)}` : directUrl;
  // TikTok-style cache: play from an in-memory blob when we have one so re-scrolling
  // never re-downloads the clip. Falls back to the plain URL when not cached yet.
  const [blobSrc, setBlobSrc] = useState(() => (isHls ? null : getReelBlob(resolveMediaUrl(videoSrc))));
  const readyRef = useRef(false);
  useEffect(() => { readyRef.current = ready; }, [ready]);
  // Adopt a cached/prefetched blob for THIS reel so re-scrolling never re-downloads it.
  // IMPORTANT: never swap the src of the ACTIVE (playing/loading) card — that aborts its
  // load and restarts. We only adopt on adjacent cards / fresh mounts; by the time the
  // card becomes active it already points at the in-memory blob (zero re-download).
  useEffect(() => {
    if (isHls || !directUrl) return undefined;
    const cached = getReelBlob(directUrl);
    if (cached) { if (cached !== blobSrc && !active && !readyRef.current) setBlobSrc(cached); return undefined; }
    if (near) {
      let alive = true;
      prefetchReel(directUrl).then((b) => { if (alive && b && !active && !readyRef.current) setBlobSrc(b); });
      return () => { alive = false; };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [near, active, isHls, directUrl]);
  const fmtT = (s) => { s = Math.max(0, Math.floor(s || 0)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };
  const flashControls = () => {
    setShowControls(true);
    clearTimeout(controlsTimer.current);
    controlsTimer.current = setTimeout(() => { if (videoRef.current && !videoRef.current.paused) setShowControls(false); }, 2600);
  };
  const togglePlay = () => {
    const v = videoRef.current; if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setPaused(false); flashControls(); }
    else { v.pause(); setPaused(true); setShowControls(true); }
  };
  // Recouvert (chat plein écran mobile, app en arrière-plan) : pause SANS perdre la position ; reprise seulement si
  // c'est nous qui avions mis en pause (un reel arrêté par le membre reste arrêté).
  const coverPausedRef = useRef(false);
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !active) { coverPausedRef.current = false; return; }
    if (covered) { if (!v.paused) { coverPausedRef.current = true; v.pause(); } }
    else if (coverPausedRef.current) { coverPausedRef.current = false; v.play().catch(() => {}); }
  }, [covered, active]);
  const skip = (sec) => {
    const v = videoRef.current; if (!v) return;
    try { v.currentTime = Math.max(0, Math.min((v.duration || 0), v.currentTime + sec)); } catch { /* noop */ }
    flashControls();
  };
  const onSeek = (e) => {
    const v = videoRef.current; if (!v || !v.duration) return;
    const val = Number(e.target.value);
    v.currentTime = (val / 100) * v.duration;
    setProgress(val); setCurTime(v.currentTime); flashControls();
  };
  // Resume playback: once the active reel's duration is known, seek to the saved position.
  const tryResume = () => {
    const v = videoRef.current;
    if (!v || !active || resumedRef.current || !v.duration) return;
    const target = resumeTargetRef.current;
    // Déjà positionné (hls.js startPosition / fragment #t= / lecteur gardé en mémoire) → aucun seek.
    if (target > 1 && target < v.duration - 1 && Math.abs(v.currentTime - target) > 2.5) { try { v.currentTime = target; } catch { /* noop */ } }
    resumedRef.current = true;
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (active) {
      startT0Ref.current = performance.now();
      startDoneRef.current = false;
      startPreRef.current = v.readyState >= 2 ? 1 : 0;
      // Resume where the member left off (admin switch « Reprise de lecture »).
      // The seek happens in tryResume() only AFTER metadata is loaded, so the
      // video never "jumps" mid-load (the bug that disabled the first version).
      const saved = resumeAt(resumeEnabled ? getReelProgress(reel.id) : 0);
      if (saved > 1) {
        resumeTargetRef.current = saved;
        resumedRef.current = false;
        tryResume(); // kept-alive players already have metadata → instant seek
      } else {
        resumeTargetRef.current = 0;
        resumedRef.current = true;
        try { v.currentTime = 0; } catch { /* noop */ }
      }
      // Play; if the browser blocks UNMUTED autoplay (autoplay policy), fall back to muted
      // playback so the reel keeps playing instead of freezing on a paused frame (Facebook-style).
      // ONLY NotAllowedError (autoplay policy) triggers the fallback: an AbortError
      // (pause() after a swipe) must never restart a reel that left the screen —
      // that replayed hidden reels in the background (ghost audio + wasted bandwidth).
      v.play().then(() => setPaused(false)).catch((err) => {
        if (err?.name !== "NotAllowedError" || !activeRef.current) return;
        if (!v.muted) { v.muted = true; onAutoplayBlocked?.(); v.play().then(() => setPaused(false)).catch(() => {}); }
      });
      if (countView && !viewedRef.current) {
        viewedRef.current = true;
        api.post(`/reels/${reel.id}/view`).then(({ data }) => { if (typeof data?.views_count === "number") setViews(data.views_count); }).catch(() => {});
      }
    } else {
      startDoneRef.current = true;
      // Remember the position so returning to this reel resumes instead of restarting.
      if (resumeEnabled) saveReelProgress(reel.id, v.currentTime, v.duration);
      v.pause();
      setProgress(0);
      setCurTime(0);
      setPaused(false);
      setShowControls(false);
      setCaptionOpen(false);
      setPosterFaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, countView, reel.id]);

  // Save the last position when the card unmounts (e.g. leaving the Reels feed) and hand
  // the ACTIVE reel's second over to its inline teaser (feed card / discover tile).
  // Layout effect: on unmount React nulls `videoRef` BEFORE passive (useEffect) cleanups run.
  useLayoutEffect(() => {
    return () => {
      const v = videoRef.current;
      if (!v) return;
      if (resumeEnabled) saveReelProgress(reel.id, v.currentTime, v.duration);
      if (activeRef.current) setReelHandoff(reel.id, v.currentTime, v.duration);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reel.id]);

  // Preload adjacent videos so swiping to the next reel is instant (TikTok-style).
  useEffect(() => {
    const v = videoRef.current;
    if (v && near && !active && !isHls && v.readyState === 0) {
      try { v.load(); } catch {}
    }
  }, [near, active, isHls]);

  // HLS (Cloudflare Stream): attach via hls.js for adjacent + active reels so
  // playback is instant + adaptive. Off-screen reels don't fetch the manifest.
  const activeRef = useRef(active);
  activeRef.current = active;
  // Keep the hls.js player ALIVE (paused, buffer intact) while the reel stays inside
  // the prefetch window OR the "recently visited" keep window — swiping back replays
  // instantly instead of rebuilding the player from scratch. `hlsAlive` is computed
  // as one boolean so moving from `near` to `keep` never re-mounts the player.
  const hlsAlive = near || keep;
  // Neighbour budget (TikTok-style): a non-active reel buffers ONE segment (~4 s) so the
  // swipe is instant but the bandwidth stays with the reel being watched; the budget
  // is widened live when the reel becomes active (hls.js reads config on every tick).
  const hlsRef = useRef(null);
  useEffect(() => {
    const h = hlsRef.current;
    if (!h) return;
    const neighbourBudget = startAt > 0 ? NEIGHBOUR_RESUME_BUFFER_S : NEIGHBOUR_BUFFER_S;
    h.config.maxBufferLength = active ? 30 : neighbourBudget;
    h.config.maxMaxBufferLength = active ? 60 : neighbourBudget;
  }, [active]);
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isHls || !hlsAlive) return;
    setReady(false); setFailed(false);
    const cleanup = attachHls(v, resolveMediaUrl(videoSrc), {
      onReady: () => setReady(true),
      autoPlay: () => activeRef.current,
      onAutoplayBlocked,
      onHls: (h) => {
        hlsRef.current = h;
        // hls.js peut arriver (chunk différé) APRÈS que le reel soit devenu actif → budget aligné tout de suite.
        const budget = startAt > 0 ? NEIGHBOUR_RESUME_BUFFER_S : NEIGHBOUR_BUFFER_S;
        h.config.maxBufferLength = activeRef.current ? 30 : budget;
        h.config.maxMaxBufferLength = activeRef.current ? 60 : budget;
      },
      // Reprise de lecture : hls.js charge d'abord le segment de la reprise (voir attachHls).
      startPosition: startAt > 0 ? startAt : 0,
      // iPhone (lecteur natif) : le backend sert le master Bunny avec en tête la meilleure variante que le
      // réseau MESURÉ et l'écran permettent (repli Wi-Fi 480p / données 360p) — voir videoSource.js.
      startHeight: nativeStartHeight(),
      // Android/web (hls.js) : qualité d'abord — départ à la qualité que le réseau mesuré permet, plafonnée
      // à la taille du cadre ; réseau inconnu → segment de mesure ; 2G → la plus basse (voir videoSource.js).
      // Fonction : évaluée au branchement réel (l'état actif peut avoir changé pendant le chargement du chunk).
      hlsConfig: () => (activeRef.current ? undefined : { maxBufferLength: startAt > 0 ? NEIGHBOUR_RESUME_BUFFER_S : NEIGHBOUR_BUFFER_S, maxMaxBufferLength: startAt > 0 ? NEIGHBOUR_RESUME_BUFFER_S : NEIGHBOUR_BUFFER_S }),
    });
    return () => { hlsRef.current = null; cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHls, hlsAlive, videoSrc]);

  // Keep the <video> muted state authoritative (React's muted prop is unreliable).
  // When background music is present it carries the sound, so the video stays muted.
  useEffect(() => { const v = videoRef.current; if (v) v.muted = videoElMuted; }, [videoElMuted]);

  // Background music: an <audio> synced to the reel video's play/pause, with the
  // chosen volume + start offset and a smooth fade — mirrors the feed post player.
  useEffect(() => {
    const v = videoRef.current;
    const a = audioRef.current;
    if (!v || !a || !hasMusic) return undefined;
    a.src = resolveMediaUrl(reel.music_url);
    a.loop = !musicStart;
    a.muted = muted;
    a.volume = 0;
    const startAt = musicStart || 0;
    const fader = createMusicFader(a, {});
    fader.setTarget(musicVolume);
    fader.setStart(startAt);
    fader.setGate(0);
    let pauseT = null;
    const onPlay = () => { if (pauseT) { clearTimeout(pauseT); pauseT = null; } try { a.currentTime = startAt; } catch { /* noop */ } a.play().then(() => { setMusicPlaying(true); fader.fadeIn(); }).catch(() => {}); };
    const onPause = () => { fader.fadeOut(); if (pauseT) clearTimeout(pauseT); pauseT = setTimeout(() => { try { a.pause(); } catch { /* noop */ } }, 320); setMusicPlaying(false); };
    const onEnded = () => { if (startAt) { try { a.currentTime = startAt; a.play().catch(() => {}); } catch { /* noop */ } } };
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    if (!v.paused) onPlay();
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
      if (pauseT) clearTimeout(pauseT);
      fader.destroy();
      try { a.pause(); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMusic, reel.music_url, musicVolume, musicStart]);

  useEffect(() => { if (audioRef.current) audioRef.current.muted = muted; }, [muted]);

  // Lot 599 — cœur optimiste : affichage immédiat, retour arrière si le réseau échoue.
  // Lot 628 — réseau lent : un seul envoi à la fois (un 2e toucher pendant l'envoi ne retire plus la réaction),
  // et le compteur affiché ne peut jamais être 0 quand le cœur est rouge.
  const likeBusy = useRef(false);
  const shownLikes = Math.max(likes, liked ? 1 : 0);
  const likeQueued = useQueuedOffline(`post:${reel.id}`);
  // Lot 630 — hors ligne : le cœur reste, l'envoi est mis en file (dernier choix gagnant) et repart au retour du réseau.
  const queueLike = (on) => enqueueOffline({ kind: "reel_like", key: `post:${reel.id}`, method: on ? "post" : "delete",
    url: `/posts/${reel.id}/reactions`, body: on ? { type: "love" } : null, meta: { post_id: reel.id, type: on ? "love" : null } });
  const toggleLike = async () => {
    if (!user) { requireLogin("react"); return; }
    if (likeBusy.current) return;
    likeBusy.current = true;
    const was = liked;
    setLiked(!was); setLikes((l) => (was ? Math.max(0, l - 1) : l + 1));
    if (isOffline()) { queueLike(!was); likeBusy.current = false; return; }
    try {
      if (was) await api.delete(`/posts/${reel.id}/reactions`, { timeout: 20000 });
      else await api.post(`/posts/${reel.id}/reactions`, { type: "love" }, { timeout: 20000 });
    } catch (e) {
      if (shouldQueue(e)) { queueLike(!was); return; }
      setLiked(was); setLikes((l) => (was ? l + 1 : Math.max(0, l - 1)));
      toast.error(t("common.error", "Erreur"));
    } finally { likeBusy.current = false; }
  };

  // Instagram/TikTok-style: double-tap likes (never unlikes) + animated heart burst.
  const likeOnly = async () => {
    if (!user) { requireLogin("react"); return; }
    if (liked || likeBusy.current) return;
    likeBusy.current = true;
    setLiked(true); setLikes((l) => l + 1);
    if (isOffline()) { queueLike(true); likeBusy.current = false; return; }
    try { await api.post(`/posts/${reel.id}/reactions`, { type: "love" }, { timeout: 20000 }); }
    catch (e) { if (shouldQueue(e)) { queueLike(true); return; } setLiked(false); setLikes((l) => Math.max(0, l - 1)); }
    finally { likeBusy.current = false; }
  };
  const triggerHeart = () => { setHeartBurst(true); setTimeout(() => setHeartBurst(false), 800); };
  const handleTap = () => {
    const now = Date.now();
    if (now - tapRef.current.last < 280) {
      // Double tap = like (never unlike), with heart burst.
      clearTimeout(tapRef.current.timer);
      tapRef.current.last = 0;
      triggerHeart();
      likeOnly();
    } else {
      // Single tap = play/pause the reel (Facebook-style), revealing the controls.
      tapRef.current.last = now;
      tapRef.current.timer = setTimeout(() => { togglePlay(); }, 280);
    }
  };

  const share = async () => {
    const title = t("reels.share_title", "Yalola Reel");
    const text = reel.content || t("reels.share_text", "Regarde ce reel sur Yalola 🙏");
    const ok = await sharePostCard({ postId: reel.id, title, text, copiedMessage: t("common.link_copied", "Lien copié") });
    if (ok) api.post(`/reels/${reel.id}/share`).then(({ data }) => { if (typeof data?.shares_count === "number") setShares(data.shares_count); }).catch(() => {}); // feuille annulée → pas de partage compté
  };

  const genSubs = async () => {
    if (genLoading) return;
    setGenLoading(true);
    try {
      const { data } = await api.post(`/reels/${reel.id}/subtitles`);
      const segs = data.captions || [];
      setCaptions(segs);
      setCcOn(true);
      if (segs.length) toast.success(t("reels.subs_done", "Sous-titres générés"));
      else toast.info(t("reels.subs_none", "Aucune parole détectée dans ce Reel"));
    } catch { toast.error(t("common.error", "Erreur")); }
    finally { setGenLoading(false); }
  };
  const curCaption = (ccOn && captions.length)
    ? (captions.find((c) => curTime >= c.start && curTime <= (c.end + 0.3))?.text || "")
    : "";

  const author = reel.author || {};
  const isOwner = reel.is_owner || (user && author.id && user.id === author.id);
  const canModerate = ["super_admin", "moderator"].includes(user?.role);
  const shareLocked = postShareLocked({ ...reel, visibility: audience }, user); // profil privé / audience restreinte : partage / lien réservés
  const audienceEditable = isOwner && !reel.as_church_id && !reel.group && !reel.is_ad; // lot 653

  // ⋯ menu (Facebook-style) built on existing features: save, copy link, hide, unfollow, block, report, delete.
  const toggleSave = async () => {
    try {
      if (saved) { await api.delete(`/posts/${reel.id}/save`); setSaved(false); toast.success(t("post.unsaved", "Retiré des enregistrements")); }
      else { await api.post(`/posts/${reel.id}/save`); setSaved(true); toast.success(t("post.saved", "Publication enregistrée")); }
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(toShareLink(`${publicOrigin()}/post/${reel.id}`)); toast.success(t("common.link_copied", "Lien copié")); }
    catch { toast.error(t("common.error", "Erreur")); }
  };
  const doHide = async () => {
    try { await api.post(`/posts/${reel.id}/hide`); toast.success(t("post.hidden_toast", "Publication masquée")); onDeleted?.(reel.id); }
    catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };
  const doUnfollow = async () => {
    try { await api.delete(`/users/${author.id}/follow`); toast.success(t("post.unfollowed_toast", "Vous ne suivez plus {name}").replace("{name}", author.name || "")); }
    catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };
  const menuItems = (
    <>
      <DropdownMenuItem onClick={toggleSave} data-testid={`reel-save-${reel.id}`}>
        <Bookmark size={15} className="mr-2" fill={saved ? "currentColor" : "none"} /> {saved ? t("reels.unsave", "Retirer des enregistrements") : t("reels.save", "Enregistrer le Reel")}
      </DropdownMenuItem>
      {!shareLocked && (
        <DropdownMenuItem onClick={copyLink} data-testid={`reel-copy-link-${reel.id}`}>
          <Link2 size={15} className="mr-2" /> {t("common.copy_link", "Copier le lien")}
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      {isOwner ? (
        <>
          {audienceEditable && (
            <DropdownMenuItem onClick={() => setAudienceOpen(true)} data-testid={`reel-audience-edit-${reel.id}`}>
              {React.createElement(audienceOf(audience).icon, { size: 15, className: "mr-2" })} {t("audience.edit", "Modifier l'audience")} · {t(audienceOf(audience).tkey, audienceOf(audience).label)}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setConfirmDel(true)} className="text-red-600 focus:text-red-600" data-testid={`reel-delete-${reel.id}`}>
            <Trash2 size={15} className="mr-2" /> {t("common.delete", "Supprimer")}
          </DropdownMenuItem>
        </>
      ) : (
        <>
          <DropdownMenuItem onClick={doHide} className="items-start" data-testid={`reel-hide-${reel.id}`}>
            <EyeOff size={15} className="mr-2 mt-0.5 shrink-0" />
            <span className="flex flex-col min-w-0">
              <span>{t("reels.not_interested", "Pas intéressé(e)")}</span>
              <span className="text-[11px] opacity-70 font-normal">{t("reels.not_interested_desc", "Ce Reel ne s'affichera plus dans ton fil")}</span>
            </span>
          </DropdownMenuItem>
          {author.id && (
            <DropdownMenuItem onClick={doUnfollow} data-testid={`reel-unfollow-${reel.id}`}>
              <UserX size={15} className="mr-2" /> {t("post.unfollow", "Ne plus suivre {name}").replace("{name}", author.name || "")}
            </DropdownMenuItem>
          )}
          {author.id && !author.is_protected && (
            <DropdownMenuItem onClick={() => setBlockOpen(true)} className="text-red-600 focus:text-red-600" data-testid={`reel-block-${reel.id}`}>
              <Ban size={15} className="mr-2" /> {t("block.action", "Bloquer {name}").replace("{name}", author.name || "")}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {canModerate && (
            <DropdownMenuItem onClick={() => setConfirmDel(true)} className="text-red-600 focus:text-red-600" data-testid={`reel-moderate-delete-${reel.id}`}>
              <Trash2 size={15} className="mr-2" /> {t("post.mod_delete", "Supprimer (modération)")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setReportOpen(true)} className="text-red-600 focus:text-red-600" data-testid={`reel-report-${reel.id}`}>
            <Flag size={15} className="mr-2" /> {t("reels.report", "Signaler le Reel")}
          </DropdownMenuItem>
        </>
      )}
    </>
  );

  const doDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/posts/${reel.id}`);
      toast.success(t("reels.deleted", "Reel supprimé"));
      setConfirmDel(false);
      onDeleted?.(reel.id);
    } catch (e) {
      toast.error(e?.friendlyMessage || t("common.error", "Erreur"));
    } finally { setDeleting(false); }
  };

  const doReport = async (reason, category) => {
    if (reporting) return;
    setReporting(true);
    try {
      await api.post(`/posts/${reel.id}/report`, { reason, category });
      toast.success(t("post.reported_toast", "Merci, la publication a été signalée aux modérateurs"));
      setReportOpen(false);
    } catch (e) {
      toast.error(e?.friendlyMessage || t("common.error", "Erreur"));
    } finally { setReporting(false); }
  };

  const posterUrl = reel.poster_url ? resolveMediaUrl(reel.poster_url) : (isHls ? hlsPoster(reel.video_url) : undefined);
  const poster = posterBroken ? undefined : posterUrl;

  return (
    <div className={`reel-card${showComments ? " reel-card--comments" : ""}`} data-testid={`reel-${reel.id}`} data-comments={showComments ? "1" : undefined}>
      {/* Blurred backdrop fills letterbox gaps for non-9:16 videos (YouTube Shorts / IG Reels style) */}
      {poster && near && (
        <div
          className="reel-backdrop absolute inset-0 pointer-events-none"
          style={{ backgroundImage: `url(${poster})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(16px)", transform: "scale(1.08)" }}
          aria-hidden
        />
      )}
      <video
        ref={videoRef}
        src={isHls ? undefined : (blobSrc || directSrc)}
        poster={poster || NEUTRAL_POSTER}
        loop
        muted={videoElMuted}
        playsInline
        preload={near ? "auto" : "none"}
        onLoadedData={() => setReady(true)}
        onCanPlay={() => { setReady(true); tryResume(); }}
        onPlaying={() => { setReady(true); tryResume(); setPosterFaded(true); if (!startDoneRef.current) { startDoneRef.current = true; recordReelStart(performance.now() - startT0Ref.current, isHls ? "hls" : "mp4", { pre: startPreRef.current, views: reel.views_count || 0, pl: isHls ? lastPlayerUsed() : "mp4" }); } }}
        onLoadedMetadata={() => { const v = videoRef.current; if (v && v.duration) setDuration(v.duration); tryResume(); }}
        onDurationChange={() => { const v = videoRef.current; if (v && v.duration) setDuration(v.duration); }}
        onPlay={() => setPaused(false)}
        onPause={() => setPaused(true)}
        onError={() => {
          if (isHls) return;
          // A dead/evicted blob URL → drop it and retry from the direct URL once.
          if (blobSrc) { dropReelBlob(directUrl); setBlobSrc(null); }
          else setFailed(true);
        }}
        onTimeUpdate={() => {
          const v = videoRef.current;
          if (!v || !v.duration) return;
          if (!scrubbing.current) {
            // Rendu seulement quand l'affichage change (≈ 2 valeurs/s) : timeupdate tire 4–60×/s et re-rendait toute la carte.
            const pct = Math.min(100, Math.round((v.currentTime / v.duration) * 400) / 4);
            const sec = Math.floor(v.currentTime);
            if (pct !== lastPctRef.current) { lastPctRef.current = pct; setProgress(pct); }
            if (sec !== lastSecRef.current) { lastSecRef.current = sec; setCurTime(sec); }
          }
          if (active && resumeEnabled) { const now = Date.now(); if (now - lastSaveRef.current > 2000) { lastSaveRef.current = now; saveReelProgress(reel.id, v.currentTime, v.duration); } }
        }}
        className="reel-video absolute inset-x-0 top-0 w-full h-full object-contain transition-opacity duration-300 ease-out"
        style={{ opacity: (ready || poster) ? 1 : 0, ...(fxStyle(reel.video_fx) || {}) }}
        onClick={showComments ? () => setShowComments(false) : handleTap}
      />
      {/* Hidden background-music element (synced to the reel video in an effect above) */}
      {hasMusic && <audio ref={audioRef} className="hidden" data-testid={`reel-music-audio-${reel.id}`} />}

      {/* First-frame fade (TikTok): sharp poster shown full-screen instantly, then a
          quick 200ms fade to the live video on the first 'playing' event. Purely
          visual — never intercepts taps. */}
      {poster && !failed && (
        <img
          src={poster}
          alt=""
          aria-hidden
          onError={() => setPosterBroken(true)}
          className="reel-video absolute inset-x-0 top-0 w-full h-full object-contain z-[1] pointer-events-none transition-opacity duration-200 ease-out"
          style={{ opacity: posterFaded ? 0 : 1, ...(fxStyle(reel.video_fx) || {}) }}
          data-testid={`reel-poster-fade-${reel.id}`}
        />
      )}

      {/* While the first frame decodes, the <video>'s own poster shows a SHARP
          thumbnail (TikTok/IG feel). We only fall back to a dark skeleton when there
          is no poster at all — no more heavy full-screen blur wall. */}
      {!ready && !failed && !poster && (
        <div className="absolute inset-0 z-[2] pointer-events-none" data-testid={`reel-loading-${reel.id}`}>
          <div className="absolute inset-0 yalola-skeleton" style={{ background: "#0b0b0d" }} />
        </div>
      )}

      {/* Video failed to load — never hang on the placeholder forever */}
      {failed && (
        <div className="absolute inset-0 z-[2] grid place-items-center bg-black px-8 text-center" data-testid={`reel-error-${reel.id}`}>
          {poster && (
            <div className="absolute inset-0" style={{ backgroundImage: `url(${poster})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.35 }} />
          )}
          <div className="relative text-white/80">
            <Film size={40} className="mx-auto mb-3 opacity-70" />
            <p className="text-sm">{t("reels.load_error", "Vidéo indisponible pour le moment.")}</p>
          </div>
        </div>
      )}

      {overlays && <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.28) 0%, transparent 26%, transparent 58%, rgba(0,0,0,0.78) 100%)" }} />}

      {/* Double-tap heart burst */}
      {heartBurst && (
        <div className="absolute inset-0 z-20 grid place-items-center pointer-events-none" data-testid={`reel-heart-burst-${reel.id}`}>
          <Heart size={120} fill="#E11D48" color="#E11D48" className="reel-heart-pop" style={{ filter: "drop-shadow(0 6px 18px rgba(0,0,0,0.45))" }} />
        </div>
      )}

      {/* Facebook-style center controls: ‑10s · play/pause · +10s (tap toggles) */}
      {overlays && ready && !failed && (showControls || paused) && (
        <div className="absolute inset-0 z-[16] flex items-center justify-center gap-7 pointer-events-none" data-testid={`reel-controls-${reel.id}`}>
          <button
            onClick={(e) => { e.stopPropagation(); skip(-10); }}
            className="pointer-events-auto relative h-12 w-12 grid place-items-center rounded-full bg-black/45 text-white backdrop-blur-sm transition-transform active:scale-90"
            aria-label="-10s" data-testid={`reel-back10-${reel.id}`}
          >
            <RotateCcw size={26} />
            <span className="absolute text-[8px] font-bold">10</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); togglePlay(); }}
            className="pointer-events-auto h-16 w-16 grid place-items-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-transform active:scale-90"
            aria-label={paused ? "Lire" : "Pause"} data-testid={`reel-playpause-${reel.id}`}
          >
            {paused ? <Play size={30} fill="white" /> : <Pause size={30} fill="white" />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); skip(10); }}
            className="pointer-events-auto relative h-12 w-12 grid place-items-center rounded-full bg-black/45 text-white backdrop-blur-sm transition-transform active:scale-90"
            aria-label="+10s" data-testid={`reel-fwd10-${reel.id}`}
          >
            <RotateCw size={26} />
            <span className="absolute text-[8px] font-bold">10</span>
          </button>
        </div>
      )}

      {overlays && <SoundControl
        muted={muted}
        onToggle={onToggleMute}
        hasAudio={hasMusic ? true : reel.has_audio}
        variant="full"
        className="reel-top-right absolute right-3 z-30"
        style={{ top: "calc(var(--sa-top) + 3.25rem)" }}
        hintLabel={t("video.tap_for_sound", "Appuyez pour le son")}
        noAudioLabel={t("video.no_sound", "Sans son")}
        muteLabel={t("video.mute", "Couper le son")}
        testId={`reel-sound-${reel.id}`}
      />}

      {/* ⋯ options menu — top-right on phones/tablets; on PC it lives in the rail beside the video (CSS) */}
      {overlays && user && (
        <div className="reel-menu-top reel-top-right absolute right-3 z-30" style={{ top: "calc(var(--sa-top) + 6rem)" }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="h-9 w-9 grid place-items-center rounded-full bg-black/40 text-white backdrop-blur-sm"
                data-testid={`reel-menu-${reel.id}`}
                aria-label={t("common.more", "Plus d'options")}
              >
                <MoreVertical size={18} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64" data-testid={`reel-menu-content-${reel.id}`}>
              {menuItems}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Right action rail (TikTok-style) */}
      {overlays && <div className="reel-actions absolute right-3 z-10 flex flex-col items-center gap-5 text-white">
        <button onClick={toggleLike} className="flex flex-col items-center transition-transform active:scale-90" data-testid={`reel-like-${reel.id}`}>
          <span className="reel-action-icon h-11 w-11 grid place-items-center rounded-full bg-black/30 backdrop-blur-sm">
            <Heart size={26} fill={liked ? "#E11D48" : "none"} color={liked ? "#E11D48" : "white"} />
          </span>
          <span className="text-xs mt-1 font-semibold drop-shadow inline-flex items-center gap-1">{compactNumber(shownLikes)}{likeQueued && <WifiOff size={11} className="opacity-80" aria-label={t("offline.waiting", "En attente du réseau")} data-testid={`reel-like-offline-${reel.id}`} />}</span>
        </button>
        <button onClick={() => setShowComments(true)} className="flex flex-col items-center transition-transform active:scale-90" data-testid={`reel-comment-${reel.id}`}>
          <span className="reel-action-icon h-11 w-11 grid place-items-center rounded-full bg-black/30 backdrop-blur-sm">
            <MessageCircle size={25} />
          </span>
          <span className="text-xs mt-1 font-semibold drop-shadow">{commentCount}</span>
        </button>
        {!shareLocked && (
          <button onClick={share} className="flex flex-col items-center transition-transform active:scale-90" data-testid={`reel-share-${reel.id}`} data-count={shares}>
            <span className="reel-action-icon h-11 w-11 grid place-items-center rounded-full bg-black/30 backdrop-blur-sm">
              <Share2 size={23} />
            </span>
            <span className="text-xs mt-1 font-semibold drop-shadow" data-testid={`reel-share-count-${reel.id}`}>{compactNumber(shares)}</span>
          </button>
        )}
        {(captions.length > 0 || isOwner) && (
          <button
            onClick={(e) => { e.stopPropagation(); if (captions.length) setCcOn((o) => !o); else genSubs(); }}
            className="flex flex-col items-center transition-transform active:scale-90"
            data-testid={`reel-cc-${reel.id}`}
            aria-label={t("reels.subtitles", "Sous-titres")}
          >
            <span className={`reel-action-icon h-11 w-11 grid place-items-center rounded-full backdrop-blur-sm ${captions.length && ccOn ? "bg-white/95 text-black" : "bg-black/30 text-white"}`}>
              {genLoading ? <Loader2 size={22} className="animate-spin" /> : <Captions size={23} />}
            </span>
            <span className="text-[10px] mt-1 font-semibold drop-shadow">
              {captions.length ? "CC" : t("reels.subs_gen_short", "Générer")}
            </span>
          </button>
        )}
        <div className="flex flex-col items-center" data-testid={`reel-views-${reel.id}`}>
          <span className="reel-action-icon h-11 w-11 grid place-items-center rounded-full bg-black/30 backdrop-blur-sm">
            <Eye size={23} />
          </span>
          <span className="text-xs mt-1 font-semibold drop-shadow">{views}</span>
        </div>
        {user && (
          <div className="reel-menu-rail flex-col items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="reel-action-icon h-11 w-11 grid place-items-center rounded-full bg-black/30 backdrop-blur-sm transition-transform active:scale-90" data-testid={`reel-menu-rail-${reel.id}`} aria-label={t("common.more", "Plus d'options")}>
                  <MoreVertical size={22} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="left" align="end" className="w-64" data-testid={`reel-menu-rail-content-${reel.id}`}>
                {menuItems}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>}

      {/* Live subtitles (CC) synced to playback */}
      {overlays && curCaption && (
        <div
          className="reel-cc-text absolute left-4 right-20 z-20 pointer-events-none flex justify-center"
          style={{ bottom: "6.5rem" }}
          data-testid={`reel-cc-text-${reel.id}`}
        >
          <span
            className="inline-block max-w-full text-center text-white text-sm sm:text-base font-semibold leading-snug px-2.5 py-1 rounded-lg"
            style={{ background: "rgba(0,0,0,0.6)", textShadow: "0 1px 2px rgba(0,0,0,0.9)" }}
          >
            {curCaption}
          </span>
        </div>
      )}

      {/* Author + caption */}
      {overlays && <div className="reel-caption absolute left-4 right-20 z-10 text-white">
        <Link to={author.is_church && author.church_id ? `/churches/${author.church_id}` : `/profile/${author.id}`} className="flex items-center gap-2" data-testid={`reel-author-${reel.id}`}>
          <Avatar className="h-9 w-9 border-2 border-white">
            <AvatarImage src={avatarSrc(author)} />
            <AvatarFallback>{author.name?.[0]}</AvatarFallback>
          </Avatar>
          <span className="font-semibold text-sm flex items-center gap-1 drop-shadow">
            {author.name}
            {author.is_verified && <VerifiedBadge user={author} size={13} />}
          </span>
        </Link>
        {audienceEditable && (() => { const a = audienceOf(audience); return (
          <button type="button" onClick={(e) => { e.stopPropagation(); setAudienceOpen(true); }}
            className="mt-1.5 inline-flex items-center gap-1 rounded-full backdrop-blur-sm px-2 py-0.5 text-[11px] font-semibold drop-shadow"
            style={{ background: a.accent || "rgba(0,0,0,0.4)" }}
            title={t("audience.edit", "Modifier l'audience")} data-testid={`reel-audience-${reel.id}`} data-audience={a.key}>
            <a.icon size={11} /> {t(a.tkey, a.label)}
          </button>
        ); })()}
        {reel.content && (
          <div
            onClick={(e) => { e.stopPropagation(); setCaptionOpen((o) => !o); }}
            className={`mt-2 text-sm leading-snug drop-shadow cursor-pointer ${captionOpen ? "max-h-[35vh] overflow-y-auto pr-1 hide-scrollbar" : "line-clamp-2"}`}
            data-testid={`reel-caption-${reel.id}`}
          >
            {renderRichText(reel.content)}
          </div>
        )}
        {hasMusic && (
          <div
            className="mt-2 inline-flex items-center gap-1.5 max-w-full rounded-full bg-black/40 backdrop-blur-sm px-2.5 py-1 text-xs font-semibold drop-shadow"
            data-testid={`reel-music-badge-${reel.id}`}
          >
            <Music size={13} className="shrink-0" />
            <span className={`eq ${musicPlaying && !muted ? "eq-on" : ""}`} aria-hidden data-testid={`reel-music-eq-${reel.id}`}><i /><i /><i /><i /></span>
            <span className="truncate">{reel.music_title || t("post.music", "Musique")}</span>
          </div>
        )}
      </div>}

      {/* Playback scrubber (seekable) + time — Facebook-style, above the bottom nav */}
      {overlays && !failed && (
      <div
        className="reel-progress absolute left-0 right-0 z-20 px-3"
        data-testid={`reel-progress-${reel.id}`}
        onClick={(e) => e.stopPropagation()}
      >
        {(showControls || paused) && (
          <div className="flex items-center justify-end text-[11px] font-semibold text-white/95 drop-shadow mb-1 px-0.5" data-testid={`reel-time-${reel.id}`}>
            {fmtT(curTime)} / {fmtT(duration)}
          </div>
        )}
        <input
          type="range" min="0" max="100" step="0.1"
          value={progress}
          onChange={onSeek}
          onPointerDown={() => { scrubbing.current = true; flashControls(); }}
          onPointerUp={() => { scrubbing.current = false; }}
          onPointerCancel={() => { scrubbing.current = false; }}
          className="reel-range w-full"
          style={{ "--reel-fill": `${progress}%` }}
          aria-label="Progression"
          data-testid={`reel-seek-${reel.id}`}
        />
      </div>
      )}

      {/* TikTok-style comments sheet: video stays playing in the top band (tap it to close) */}
      {showComments && (
        <div className="reel-comments-sheet" data-testid={`reel-comments-sheet-${reel.id}`}>
          <CommentModal post={reel} open embedded onOpenChange={setShowComments} onCountChange={(d) => setCommentCount((c) => Math.max(0, c + d))} />
        </div>
      )}

      {/* Delete confirmation — in-card overlay (z-[60]) so it sits above the reel */}
      {confirmDel && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center p-6" data-testid={`reel-delete-overlay-${reel.id}`}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !deleting && setConfirmDel(false)} />
          <div className="relative w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="text-lg font-bold" style={{ color: "var(--yalola-text)" }}>{t("reels.delete_title", "Supprimer ce Reel ?")}</h3>
            <p className="text-sm mt-1.5" style={{ color: "var(--yalola-text-muted)" }}>{t("reels.delete_desc", "Cette action est définitive. La vidéo et ses statistiques seront supprimées.")}</p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setConfirmDel(false)}
                disabled={deleting}
                className="rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-50"
                style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text)" }}
                data-testid={`reel-delete-cancel-${reel.id}`}
              >
                {t("common.cancel", "Annuler")}
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={deleting}
                className="rounded-full px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                data-testid={`reel-delete-confirm-${reel.id}`}
              >
                {deleting ? "…" : t("common.delete", "Supprimer")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        onReport={doReport}
        reporting={reporting}
        testidPrefix={`reel-report-${reel.id}`}
        title={t("reels.report_title", "Signaler ce Reel")}
      />
      <BlockUserDialog open={blockOpen} onOpenChange={setBlockOpen} user={author} testidPrefix={`reel-block-${reel.id}`} />
      {audienceEditable && (
        <Dialog open={audienceOpen} onOpenChange={setAudienceOpen}>
          <DialogContent variant="sheet" className="sm:max-w-md" data-testid={`reel-audience-dialog-${reel.id}`}>
            <DialogHeader>
              <DialogTitle>{t("reels.audience_title", "Qui peut voir ce Reel ?")}</DialogTitle>
              <DialogDescription>{t("audience.edit_hint", "Le changement s'applique tout de suite, pour tous.")}</DialogDescription>
            </DialogHeader>
            <DialogBody><AudienceOptions value={audience} onPick={changeAudience} t={t} tone="light" busy={audienceBusy} /></DialogBody>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
});

// Sponsored reel injected in the Reels feed (Facebook/TikTok-style native ad).
const SponsoredReelCard = ({ ad, active, covered = false, muted, onToggleMute, onAutoplayBlocked }) => {
  const { t, openChat } = useApp();
  const nav = useNavigate();
  const videoRef = useRef(null);
  const seenRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const cta = ad.ad || {};
  const author = ad.author || {};
  const isVideo = !!ad.video_url;
  const isHls = isVideo && isHlsSrc(ad.video_url);
  const poster = ad.poster_url ? resolveMediaUrl(ad.poster_url) : (isHls ? hlsPoster(ad.video_url) : (ad.images?.[0] ? resolveMediaUrl(ad.images[0]) : undefined));
  const hasButton = cta.type && cta.type !== "none";

  const activeRef = useRef(active);
  activeRef.current = active;
  useEffect(() => {
    const v = videoRef.current;
    if (isVideo && v) {
      if (active) v.play().catch((err) => {
        // Muted fallback ONLY for a real autoplay block, and only while still on screen
        // (an aborted play after a swipe must not restart a hidden ad — ghost audio).
        if (err?.name !== "NotAllowedError" || !activeRef.current) return;
        if (!v.muted) { v.muted = true; onAutoplayBlocked?.(); v.play().catch(() => {}); }
      });
      else { v.pause(); try { v.currentTime = 0; } catch { /* noop */ } }
    }
    if (active && !seenRef.current && cta.id) {
      seenRef.current = true;
      api.post(`/ads/${cta.id}/impression`).catch(() => {});
    }
  }, [active, isVideo, cta.id]);

  // Façon Facebook : le bouton d'action ouvre le chat / le formulaire PAR-DESSUS le reel → la pub se met en pause
  // (plus de vidéo qui tourne en boucle avec le son sous la conversation) et reprend à la même position au retour.
  const coverPausedRef = useRef(false);
  const hidden = covered || leadOpen;
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isVideo || !active) { coverPausedRef.current = false; return; }
    if (hidden) { if (!v.paused) { coverPausedRef.current = true; v.pause(); } }
    else if (coverPausedRef.current) { coverPausedRef.current = false; v.play().catch(() => {}); }
  }, [hidden, active, isVideo]);

  // HLS (Cloudflare Stream) ads play adaptively via hls.js like organic reels.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isHls) return;
    setReady(false);
    const cleanup = attachHls(v, resolveMediaUrl(ad.video_url), {
      onReady: () => setReady(true),
      autoPlay: () => activeRef.current,
      onAutoplayBlocked,
    });
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHls, ad.video_url]);

  useEffect(() => { if (videoRef.current) videoRef.current.muted = muted; }, [muted]);

  // Sponsored video analytics: starts + 25/50/75/100% milestones + watch seconds.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isVideo || !cta.id) return;
    return attachAdVideoTracking(v, cta.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideo, cta.id]);

  return (
    <div className="reel-card" data-testid={`reel-ad-${cta.id}`}>
      {poster ? (
        <div className="reel-backdrop absolute inset-0 pointer-events-none" style={{ backgroundImage: `url(${poster})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(16px)", transform: "scale(1.08)" }} aria-hidden />
      ) : (
        <div className="reel-backdrop absolute inset-0 pointer-events-none" aria-hidden style={{ background: "radial-gradient(circle at 50% 32%, #2a2a33 0%, #17171c 58%, #0b0b0d 100%)" }} />
      )}
      {isVideo ? (
        <video
          ref={videoRef}
          src={isHls ? undefined : resolveMediaUrl(ad.video_url)}
          poster={poster}
          loop
          muted={muted}
          playsInline
          preload={isHls ? "none" : "auto"}
          onLoadedData={() => setReady(true)}
          onCanPlay={() => setReady(true)}
          onPlaying={() => setReady(true)}
          className="reel-video absolute inset-0 w-full h-full object-contain transition-opacity duration-300 ease-out"
          style={{ opacity: (ready || poster) ? 1 : 0 }}
          onClick={() => { const v = videoRef.current; if (v) (v.paused ? v.play() : v.pause()); }}
        />
      ) : (
        poster && <img src={poster} alt="" onError={onPosterError} className="reel-video absolute inset-0 w-full h-full object-contain" />
      )}

      <div className="reel-backdrop absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.3) 0%, transparent 24%, transparent 50%, rgba(0,0,0,0.85) 100%)" }} />

      {isVideo && (
        <SoundControl
          muted={muted}
          onToggle={onToggleMute}
          hasAudio={ad.has_audio}
          variant="full"
          className="reel-top-right absolute right-3 z-30"
          style={{ top: "calc(var(--sa-top) + 3.25rem)" }}
          hintLabel={t("video.tap_for_sound", "Appuyez pour le son")}
          noAudioLabel={t("video.no_sound", "Sans son")}
          muteLabel={t("video.mute", "Couper le son")}
          testId={`reel-ad-sound-${cta.id}`}
        />
      )}

      <div className="reel-caption absolute left-4 right-6 z-10 text-white">
        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide mb-2" style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }} data-testid={`reel-ad-label-${cta.id}`}>
          <Megaphone size={11} /> {t("post.sponsored", "Sponsorisé")}
        </div>
        <Link to={`/profile/${author.id}`} className="flex items-center gap-2" data-testid={`reel-ad-author-${cta.id}`}>
          <Avatar className="h-9 w-9 border-2 border-white">
            <AvatarImage src={avatarSrc(author)} />
            <AvatarFallback>{author.name?.[0]}</AvatarFallback>
          </Avatar>
          <span className="font-semibold text-sm flex items-center gap-1 drop-shadow">
            {author.name}
            {author.is_verified && <VerifiedBadge user={author} size={13} />}
          </span>
        </Link>
        {ad.content && <p className="mt-2 text-sm leading-snug drop-shadow line-clamp-2">{ad.content}</p>}
        {hasButton && (
          <button
            type="button"
            onClick={() => performAdCta(cta, { nav, openChat, author, openLead: () => setLeadOpen(true) })}
            className="mt-3 w-full flex items-center justify-center gap-1 rounded-full py-2.5 font-semibold text-sm transition-transform active:scale-95"
            style={{ background: "var(--yalola-primary)", color: "#fff" }}
            data-testid={`reel-ad-cta-${cta.id}`}
          >
            {ctaLabel(cta)} <ChevronRight size={16} />
          </button>
        )}
      </div>

      <AdLeadDialog ad={cta} open={leadOpen} onOpenChange={setLeadOpen} />
    </div>
  );
};


export const Reels = () => {
  const { t, config, user, lang, chatTarget } = useApp();
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const startId = searchParams.get("start");
  // Tapped from an inline preview (feed card / profile grid / discover strip): the reel
  // object travels in the router state → the player mounts IMMEDIATELY (no skeleton, no
  // API round-trip) while the full list loads in the background. Restarts from 0.
  const stateReel = location.state?.reel;
  const seededReel = stateReel && startId && stateReel.id === startId && (stateReel.hls_url || stateReel.video_url) ? stateReel : null;
  const cameFromPost = !!startId || !!location.state?.back;
  const goBack = () => { if (cameFromPost && window.history.length > 1) nav(-1); else nav("/home"); };
  const [reels, setReels] = useState(() => (seededReel ? [seededReel] : []));
  // Lien /reels?start=<id> partagé (barre d'adresse, Facebook, WhatsApp…) : aperçu du reel lui-même — titre auteur + légende,
  // vignette vidéo, og:url = ce lien (fini l'aperçu générique « yalola.app » partagé par toutes les pages) ; canonique = /post/<id>.
  const seoReel = startId ? (reels.find((r) => r.id === startId) || seededReel) : null;
  const seoText = seoReel ? stripRichMarkers(seoReel.content || "") : "";
  useSeo(seoReel ? {
    title: `${seoReel.author?.name || "Membre Yalola"} sur Yalola${seoText ? ` : ${excerpt(seoText, 70)}` : ""}`,
    description: seoText || `Vidéo de ${seoReel.author?.name || "un membre"} sur Yalola, le réseau chrétien qui rassemble.`,
    canonical: `/post/${seoReel.id}`, url: `/reels?start=${seoReel.id}`, type: "video.other",
    image: `${PUBLIC_ORIGIN}/api/e/post/${seoReel.id}/share.jpg`,
  } : null, [seoReel?.id]);
  useUserBlocked(useCallback((uid) => setReels((arr) => withoutAuthor(arr, uid)), []));
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(!seededReel);
  const [activeIdx, setActiveIdx] = useState(0);
  // Interrupteur admin « Rouvrir sur le dernier reel vu » (Paramètres › Reels).
  const resumeLastEnabled = config?.settings?.reels_resume_last_enabled !== false;
  // Volume memory: remember the member's last sound/mute choice across sessions.
  // Precedence: saved member choice > admin default (reels_default_sound; sound ON by default).
  const savedMuted = readMutedPref();
  const [muted, setMuted] = useState(() => (savedMuted !== null ? savedMuted : (config?.settings?.reels_default_sound) === false));
  // If the member already made a choice, don't let the admin default override it.
  const soundInitRef = useRef(savedMuted !== null);
  // Sound wake-up: when the browser forced a MUTED start (member's effective choice
  // was "sound ON"), restore the sound on the next user gesture — a tap/swipe lifts
  // the autoplay restriction. Member choices are always respected: if the saved
  // preference is "muted", nothing ever auto-unmutes. Native app is unaffected
  // (its WebView allows unmuted autoplay, so the fallback never fires there).
  const soundBlockedRef = useRef(false);
  const handleAutoplayBlocked = useCallback(() => {
    if (readMutedPref() !== true) soundBlockedRef.current = true;
    setMuted(true);
  }, []);
  useEffect(() => {
    const wake = () => {
      if (soundBlockedRef.current && readMutedPref() !== true) {
        soundBlockedRef.current = false;
        setMuted(false);
      }
    };
    window.addEventListener("pointerdown", wake, true);
    window.addEventListener("touchend", wake, true);
    return () => {
      window.removeEventListener("pointerdown", wake, true);
      window.removeEventListener("touchend", wake, true);
    };
  }, []);
  // Explicit tap on the sound button → persist the choice (autoplay-blocked fallback does NOT persist).
  const toggleMuted = useCallback(() => setMuted((m) => { const nm = !m; writeMutedPref(nm); return nm; }), []);
  // Lot 638 — touche de volume physique (+ ou −) pendant les Reels → son activé, mémorisé comme un tap sur 🔇.
  useVolumeKeyUnmute(true, VOLUME_PRIORITY.REELS, () => { soundBlockedRef.current = false; writeMutedPref(false); setMuted(false); });
  const [cycles, setCycles] = useState(1);
  // Lot 635 — PAGINATION RÉELLE : le serveur renvoie 20 reels par page (REELS_PAGE) + un curseur feed_cursor {phase, before}.
  // Avant : une seule page puis boucle infinie sur ces 20 reels → les plus anciens n'étaient JAMAIS atteints. Maintenant la
  // page suivante est demandée ~REELS_LOAD_AHEAD cartes avant la fin ; la boucle (cycles) ne reprend qu'une fois TOUT le
  // flux épuisé (page courte, curseur vide ou erreur réseau → on reboucle sur ce qui est chargé).
  const pageRef = useRef({ cursor: null, exhausted: false, busy: false, pages: 1 });
  const [pageInfo, setPageInfo] = useState({ pages: 1, exhausted: false });
  const [myLangOnly, setMyLangOnly] = useState(readMyLangOnly);
  useEffect(() => { writeMyLangOnly(myLangOnly); }, [myLangOnly]);
  const myLang = (user?.language || lang || "fr").toLowerCase().slice(0, 2);
  const mlDiscoveryOn = (config?.settings?.features?.multilingual_discovery) !== false;
  const showLangUi = (config?.settings?.features?.language_filter_ui) !== false;
  // Admin-tunable Reels playback tuning (network-aware): how many neighbours to
  // preload/keep-attached, and how many decoded clips to hold in memory.
  const reelPreload = Math.max(0, Math.min(Number(config?.settings?.reels_preload_count ?? 2), 5));
  const reelCacheSize = Math.max(2, Math.min(Number(config?.settings?.reels_cache_size ?? 10), 30));
  // Cache mémoire des reels mp4 (hors HLS) : borné à 4 clips dans l'app native (RAM), réglage admin sur le web.
  useEffect(() => { setReelCacheLimit(isNativeApp() ? Math.min(reelCacheSize, 4) : reelCacheSize); }, [reelCacheSize]);
  // App native : en quittant les Reels, les clips préchargés sont libérés (RAM rendue au Fil et à ses vidéos).
  useEffect(() => () => { if (isNativeApp()) releaseReelCache(); }, []);
  // HLS players kept alive BEHIND the active reel (instant swipe-back). Hard-capped at 3:
  // each live player holds an MSE buffer, and Android WebViews only have ~4-8 hardware
  // video decoders — keeping 10 alive could starve playback entirely on some phones.
  // Admin switch « Retour arrière instantané » (reels_keepalive_enabled) turns it off.
  const keepAliveOn = config?.settings?.reels_keepalive_enabled !== false;
  // APP NATIVE (Android ET iOS) : au plus NATIVE_LIVE_PLAYERS lecteurs vidéo vivants = actif + 1 devant + 1 derrière
  // (voisins ±1, aucun « gardé » au-delà). Web : voisins ±reels_preload_count + jusqu'à 3 gardés derrière.
  const nearSpan = isNativeApp() ? 1 : Math.max(1, reelPreload);
  const hlsKeepBehind = keepAliveOn ? Math.min(reelCacheSize, isNativeApp() ? NATIVE_LIVE_PLAYERS - 2 : 3) : 0;
  // Apply the admin "sound by default" setting ONCE, when the REAL config arrives.
  // AppContext seeds config.settings as an empty {} placeholder (truthy) before the
  // /config/public response; gate on a populated object so we don't latch on the placeholder.
  useEffect(() => {
    if (soundInitRef.current) return;
    const st = config?.settings;
    if (st && Object.keys(st).length) { soundInitRef.current = true; setMuted(st.reels_default_sound === false); }
  }, [config]);
  const containerRef = useRef(null);
  // Swipe-to-next is locked while a comments sheet is open on the active reel.
  const [commentsOpen, setCommentsOpen] = useState(false);
  // Reprise sur le dernier reel vu (lib/reelPrefs) : appliquée dès que la liste est rendue.
  const pendingResumeRef = useRef(null);
  // Index « aucun reel actif » pendant que la reprise attend son rendu (aucune carte active, voisine ou gardée).
  const RESUME_PENDING_IDX = -1000;


  useEffect(() => {
    // Keep the already-mounted (seeded) player on screen — the skeleton is only for a cold start.
    if (reels.length === 0) setLoading(true);
    prefetchedGet("/reels", { params: { ...((mlDiscoveryOn && myLangOnly) ? { lang: myLang } : {}) } })
      .then(async ({ data }) => {
        let list = data.reels || [];
        markFeedReady();
        // Lot 635 : curseur de la page suivante ; page courte ou sans curseur → flux déjà complet.
        const cursor = data.feed_cursor || null;
        pageRef.current = { cursor, exhausted: list.length < REELS_PAGE || !cursor?.before, busy: false, pages: 1 };
        setPageInfo({ pages: 1, exhausted: pageRef.current.exhausted });
        // Deep-link: /reels?start=<id> (tap a Reel in the feed/profile) → play that
        // Reel first. Move it to the front, or fetch it if the lang filter hid it.
        // The seeded reel keeps its key (`<id>#0`) so its player is NOT re-mounted.
        if (startId) {
          const idx = list.findIndex((r) => r.id === startId);
          if (idx > 0) {
            const [picked] = list.splice(idx, 1);
            list = [picked, ...list];
          } else if (idx === -1) {
            if (seededReel) list = [seededReel, ...list];
            else {
              try {
                const { data: pd } = await api.get(`/posts/${startId}`);
                if (pd?.post?.is_reel && pd.post.video_url) list = [pd.post, ...list];
              } catch { /* noop */ }
            }
          }
        }
        setReels(list);
        // Reprise : sans ?start, on rouvre sur le dernier reel vu s'il est encore dans le flux (posé après le rendu des cartes) —
        // interrupteur admin « Rouvrir sur le dernier reel vu » (reels_resume_last_enabled).
        const lastId = resumeLastEnabled && !startId && !seededReel ? readLastReel() : null;
        const resumeId = lastId && list.findIndex((r) => r.id === lastId) > 0 ? lastId : null;
        pendingResumeRef.current = resumeId;
        // Warm the video-CDN connection immediately so the very first reel SHOWN
        // starts without paying the TCP+TLS handshake.
        const first = (resumeId && list.find((r) => r.id === resumeId)) || list.find((r) => r.hls_url || r.video_url);
        if (first) preconnectVideoHost(first.hls_url || first.video_url);
        // Reprise en attente → AUCUN reel actif ni préchargé tant que le saut n'est pas posé : sinon le reel n° 1
        // démarrait (vidéo + voisins) puis on sautait → deux départs, bande passante partagée, premier reel lent.
        setCycles(1); setActiveIdx(resumeId ? RESUME_PENDING_IDX : 0);
        if (containerRef.current) containerRef.current.scrollTop = 0;
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/ads/feed").then(({ data }) => setAds(data.ads || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myLangOnly, myLang, mlDiscoveryOn, searchParams]);

  // Lot 635 : page suivante (même filtre de langue), ajoutée à la FIN de la liste (aucun décalage du reel actif : tant
  // que le flux n'est pas épuisé, cycles reste à 1). Doublons (même id) ignorés. Erreur réseau → flux considéré complet.
  const loadMoreReels = useCallback(async () => {
    const p = pageRef.current;
    if (p.busy || p.exhausted || !p.cursor?.before) return;
    p.busy = true;
    try {
      const { data } = await api.get("/reels", { params: { limit: REELS_PAGE, before: p.cursor.before, phase: p.cursor.phase || undefined, ...((mlDiscoveryOn && myLangOnly) ? { lang: myLang } : {}) } });
      const more = data.reels || [];
      p.cursor = data.feed_cursor || null;
      p.pages += 1;
      if (more.length < REELS_PAGE || !p.cursor?.before) p.exhausted = true;
      if (more.length) {
        setReels((rs) => {
          const seen = new Set(rs.map((r) => r.id));
          const add = more.filter((r) => r && r.id && !seen.has(r.id));
          return add.length ? [...rs, ...add] : rs;
        });
      }
    } catch (e) {
      console.warn("reels: page suivante indisponible", e?.message || e);
      p.exhausted = true;
    } finally {
      p.busy = false;
      setPageInfo({ pages: p.pages, exhausted: p.exhausted });
    }
  }, [mlDiscoveryOn, myLangOnly, myLang]);

  // Infinite loop: repeat the reels list so it never "ends" (TikTok feel).
  // Inject a sponsored reel every N reels (admin-configurable, discreet & skippable).
  // App native, format « native » : les SLIDES Google (insertGoogleReelSlides) s'ajoutent ensuite,
  // jamais collées à une pub Yalola ; une slide sans annonce disponible est retirée (deadGoogle).
  const [deadGoogle, setDeadGoogle] = useState(() => new Set());
  const onReelDeleted = useCallback((id) => setReels((rs) => rs.filter((x) => x.id !== id)), []);
  const items = useMemo(() => {
    if (reels.length === 0) return [];
    const every = adEveryFor("reels_ad_frequency", config, user, 6);
    const out = [];
    let adIdx = 0;
    for (let c = 0; c < cycles; c++) {
      reels.forEach((r, i) => {
        out.push({ ...r, _key: `${r.id}#${c}`, _countView: c === 0, _isAd: false });
        const globalPos = c * reels.length + i + 1;
        if (ads.length > 0 && globalPos % every === 0) {
          const ad = ads[adIdx % ads.length];
          adIdx += 1;
          out.push({ ...ad, _key: `ad-${ad.id}#${globalPos}`, _isAd: true });
        }
      });
    }
    const withGoogle = insertGoogleReelSlides(out, { settings: config?.settings, yalolaAds: ads.length });
    return deadGoogle.size ? withGoogle.filter((it) => !it._isGoogle || !deadGoogle.has(it._key)) : withGoogle;
  }, [reels, ads, cycles, config, user, deadGoogle]);
  // Lot 634 : des slides Google sont prévues → le réservoir « Reels » est rempli dès maintenant (la slide posera
  // l'annonce sans attente réseau quand elle arrivera). Sans effet si le réservoir est déjà plein.
  const hasGoogleSlides = items.some((it) => it._isGoogle);
  const settingsForWarm = config?.settings;
  useEffect(() => { if (hasGoogleSlides && user) preloadNativeAd(settingsForWarm, "reels"); }, [hasGoogleSlides, user, settingsForWarm]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const activeIdxRef = useRef(activeIdx);
  activeIdxRef.current = activeIdx;
  // Slide Google sans annonce → retirée ; si elle est déjà DERRIÈRE le reel actif (glissement rapide),
  // le défilement est décalé d'une hauteur pour que le reel affiché ne saute pas.
  const pendingShiftRef = useRef(0);
  const dropGoogleSlide = useCallback((key) => {
    const at = itemsRef.current.findIndex((it) => it._key === key);
    if (at !== -1 && at < activeIdxRef.current) pendingShiftRef.current += 1;
    setDeadGoogle((prev) => { if (prev.has(key)) return prev; const n = new Set(prev); n.add(key); return n; });
  }, []);
  useLayoutEffect(() => {
    const n = pendingShiftRef.current;
    if (!n) return;
    pendingShiftRef.current = 0;
    const c = containerRef.current;
    if (!c) return;
    c.scrollTop -= n * c.clientHeight;
    setActiveIdx((i) => Math.max(0, i - n));
  }, [items]);

  // Le scroll de reprise ne peut se poser qu'après le rendu des cartes → layout effect sur `items`
  // (le scroller et ses cartes sont rendus dans le même commit que la liste).
  useLayoutEffect(() => {
    const id = pendingResumeRef.current;
    const c = containerRef.current;
    if (!id || !c || !items.length || c.children.length < items.length) return;
    const at = items.findIndex((it) => !it._isAd && it.id === id);
    pendingResumeRef.current = null;
    if (at <= 0) { setActiveIdx(0); return; }
    c.scrollTop = at * c.clientHeight;
    setActiveIdx(at);
  }, [items]);
  // Mémorise le reel actif (jamais une pub) pour rouvrir l'onglet au même endroit — pas pendant la reprise en attente.
  useEffect(() => {
    if (pendingResumeRef.current) return;
    const cur = items[activeIdx];
    if (cur && !cur._isAd && cur.id) saveLastReel(cur.id);
  }, [activeIdx, items]);

  // Publicité Google dans les Reels (app NATIVE) : UNIQUEMENT des slides natives, déjà planifiées dans
  // `items` (insertGoogleReelSlides). Aucun interstitiel plein écran, aucun repli publicitaire : sans
  // annonce disponible, la slide se retire (dropGoogleSlide). Mode « repli seul » + campagne Yalola en
  // diffusion → Google volontairement absent, remonté une fois pour l'admin.
  useEffect(() => {
    if (ads.length > 0 && reelsAdMode(config?.settings) === "fallback") reportAdmobSkippedByYalola(config?.settings);
  }, [ads.length, config]);

  // Warm the video cache for the next reels so forward swipes play instantly (count is
  // admin-tunable via reels_preload_count).
  // Lot 593 — au-delà de la fenêtre « near » (lecteurs branchés, limités par les décodeurs Android), les 2 reels
  // suivants ont leur manifeste HLS + 1er segment mis dans le cache HTTP (aucun décodeur) : un swipe rapide ne
  // repart pas de zéro. Coupé par « Économiser mes données » et sur un lien 2G.
  useEffect(() => {
    for (let k = 1; k <= reelPreload; k++) {
      const it = items[activeIdx + k];
      if (it && !it._isAd) {
        const vs = it.hls_url || it.video_url;
        if (vs && !isHlsSrc(vs)) prefetchReel(resolveMediaUrl(vs));
      }
    }
  }, [activeIdx, items, reelPreload]);
  useEffect(() => {
    if (!items.length || dataSaverActive() || isSlowNetwork()) return undefined;
    // ~1,2 s après l'arrivée sur un reel : celui-ci a démarré, un swipe rapide annule (rien de gaspillé).
    // `itemsRef` (et non `items`) : la liste change d'identité à chaque mise à jour de config/pubs — ça ne doit pas
    // repousser le minuteur.
    const timer = setTimeout(() => {
      for (let k = nearSpan + 1; k <= nearSpan + REELS_WARM_AHEAD; k++) {
        const it = itemsRef.current[activeIdx + k];
        if (it && !it._isAd && it.hls_url) warmHlsOnce(it.hls_url);
      }
    }, 1200);
    return () => clearTimeout(timer);
  }, [activeIdx, nearSpan, items.length]);

  const onScroll = () => {
    const c = containerRef.current;
    if (!c) return;
    const idx = Math.round(c.scrollTop / c.clientHeight);
    if (idx !== activeIdx) setActiveIdx(idx);
    if (reels.length === 0) return;
    // Lot 635 : page suivante demandée en avance ; la boucle ne reprend qu'une fois tout le flux chargé.
    if (idx >= items.length - REELS_LOAD_AHEAD) loadMoreReels();
    if (idx >= items.length - 2 && pageRef.current.exhausted) setCycles((n) => n + 1);
  };
  const stepReel = (d) => {
    const c = containerRef.current;
    if (!c) return;
    c.scrollTo({ top: Math.max(0, activeIdx + d) * c.clientHeight, behavior: "smooth" });
  };
  const stepRef = useRef(stepReel);
  stepRef.current = stepReel;
  // « Encore une ? » : le Retour du téléphone (bouton Android, geste iOS, navigateur mobile) montre un reel de plus ;
  // un 2e Retour dans les 2 s quitte vraiment. Jamais quand on est venu voir UN reel précis depuis une publication.
  const isMobileWeb = useIsMobile();
  // Reel recouvert (chat plein écran sur téléphone/tablette, app en arrière-plan) → pause/reprise des cartes.
  const pageHidden = usePageHidden();
  const covered = reelCovered({ pageHidden, chatOpen: !!chatTarget, isMobile: isMobileWeb });
  const backHint = useLeaveGuard(!cameFromPost && (isNativeApp() || isMobileWeb), () => stepRef.current(1));
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable || e.target?.closest?.("[role=menu],[role=dialog]")) return;
      e.preventDefault();
      stepRef.current(e.key === "ArrowDown" ? 1 : -1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <AppShell showSidebars={false} immersive>
      {/* Transparent top overlay — brand + label (occulteur : la pub Google native est découpée dessous) */}
      <div
        className="fixed top-0 left-0 right-0 z-30 flex items-center justify-between px-4 py-3 pointer-events-none"
        style={{ paddingTop: "calc(var(--sa-top) + 0.5rem)", background: "linear-gradient(180deg, rgba(0,0,0,0.55), transparent)" }}
        data-native-occluder="reels-top"
      >
        <div className="flex items-center gap-2 pointer-events-auto">
          {cameFromPost && (
            <button
              onClick={goBack}
              className="grid place-items-center h-9 w-9 rounded-full text-white transition-transform active:scale-90"
              style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(6px)" }}
              data-testid="reels-back"
              aria-label={t("common.back", "Retour")}
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <Link to="/home" className="flex items-center gap-2" data-testid="reels-home">
            <img src="/yalola-logo.png" alt="Yalola" className="h-8 w-8 rounded-lg" />
            <span className="text-white font-bold font-display text-lg drop-shadow flex items-center gap-1"><Film size={18} /> {t("reels.title", "Reels")}</span>
          </Link>
        </div>
        <div className="flex items-center gap-2">
          {/* Lot 535 — filmer un reel : la caméra intégrée s'ouvre dans le composeur (/home), mode vidéo. */}
          <button
            type="button"
            onClick={() => { if (!user) { requireLogin("post"); return; } nav("/home?camera=video"); }}
            className="pointer-events-auto grid place-items-center h-9 w-9 rounded-full text-white transition-transform active:scale-90 md:hidden"
            style={{ background: "rgba(255,255,255,0.2)", backdropFilter: "blur(6px)" }}
            data-testid="reels-camera-btn"
            aria-label={t("camera.record_reel", "Filmer un reel")}
          >
            <Camera size={18} />
          </button>
          {mlDiscoveryOn && showLangUi && (
          <button
            onClick={() => setMyLangOnly((v) => !v)}
            className="pointer-events-auto flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold"
            style={{ background: myLangOnly ? "var(--yalola-primary)" : "rgba(255,255,255,0.2)", color: "#fff", backdropFilter: "blur(6px)" }}
            data-testid="reels-my-lang-btn"
            title={t("discover.my_lang_hint", "Afficher surtout le contenu dans ma langue")}
          >
            <Globe size={13} /> {t("discover.my_lang", "Ma langue")} · {myLang.toUpperCase()}
          </button>
          )}
        </div>
      </div>

      {loading && <ReelSkeleton />}

      {backHint && (
        <div data-testid="reels-back-hint" className="fixed left-4 right-4 z-[100000] px-5 py-3.5 rounded-2xl shadow-2xl font-semibold text-sm text-center pointer-events-none animate-in fade-in slide-in-from-bottom-2"
          style={{ bottom: "calc(var(--sa-bottom, 0px) + 4.5rem)", background: "var(--yalola-primary)", color: "#fff" }}>
          {t("reels.back_hint", "Encore une ? Appuie à nouveau sur Retour pour quitter")}
        </div>
      )}

      {!loading && reels.length === 0 && (
        <div className="h-[100dvh] grid place-items-center bg-black px-8 text-center" data-testid="reels-empty">
          <div>
            <Film size={44} className="mx-auto mb-3 text-white/80" />
            <p className="text-sm text-white/70">
              {(myLangOnly && mlDiscoveryOn)
                ? t("reels.empty_lang", "Aucun Reel dans ta langue pour l'instant.")
                : t("reels.empty", "Aucun Reel pour le moment. Publie une vidéo verticale depuis le fil pour créer le premier !")}
            </p>
            {(myLangOnly && mlDiscoveryOn) && (
              <button
                onClick={() => setMyLangOnly(false)}
                className="mt-4 rounded-full px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5"
                style={{ background: "var(--yalola-primary)", color: "#fff" }}
                data-testid="reels-show-all-langs"
              ><Globe size={14} /> {t("reels.show_all_langs", "Voir tous les Reels")}</button>
            )}
          </div>
        </div>
      )}

      {reels.length > 0 && (
        <>
          <div ref={containerRef} onScroll={onScroll} className="reels-fullscreen hide-scrollbar" style={commentsOpen ? { overflowY: "hidden" } : undefined} data-comments-open={commentsOpen ? "1" : undefined} data-testid="reels-scroller" data-reels-loaded={reels.length} data-reels-pages={pageInfo.pages} data-reels-exhausted={pageInfo.exhausted ? "1" : "0"} data-reels-cycles={cycles}>
            {items.map((r, i) => (
              // Fenêtrage (recyclage façon TikTok) : loin du reel actif, une case vide de même hauteur garde la
              // géométrie du défilement ; la carte complète (arbre React, <video>, overlays) n'existe que près de l'actif.
              Math.abs(i - activeIdx) > REEL_WINDOW && activeIdx !== RESUME_PENDING_IDX
                ? <div key={r._key} className="reel-card" aria-hidden="true" data-testid="reel-placeholder" />
                : r._isGoogle
                ? <GoogleReelSlide key={r._key} item={r} active={i === activeIdx} onUnavailable={dropGoogleSlide} />
                : r._isAd
                ? <SponsoredReelCard key={r._key} ad={r} active={i === activeIdx} covered={covered} muted={muted} onToggleMute={toggleMuted} onAutoplayBlocked={handleAutoplayBlocked} />
                : <ReelCard key={r._key} reel={r} active={i === activeIdx} covered={covered} near={Math.abs(i - activeIdx) <= nearSpan} keep={i <= activeIdx && activeIdx - i <= hlsKeepBehind} muted={muted} countView={r._countView} onToggleMute={toggleMuted} onAutoplayBlocked={handleAutoplayBlocked} onDeleted={onReelDeleted} onCommentsChange={setCommentsOpen} />
            ))}
          </div>
          {/* PC web: Facebook-style ▲ ▼ far right (CSS shows it ≥ 1024 px, never in the native shell) */}
          {!commentsOpen && (
            <div className="reel-pc-nav fixed right-6 top-1/2 -translate-y-1/2 z-30 flex-col gap-3" data-testid="reels-pc-nav">
              <button type="button" onClick={() => stepReel(-1)} disabled={activeIdx <= 0} className="h-12 w-12 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-30 transition-colors" aria-label={t("reels.prev", "Reel précédent")} data-testid="reels-pc-prev"><ChevronUp size={26} /></button>
              <button type="button" onClick={() => stepReel(1)} disabled={activeIdx >= items.length - 1} className="h-12 w-12 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-30 transition-colors" aria-label={t("reels.next", "Reel suivant")} data-testid="reels-pc-next"><ChevronDown size={26} /></button>
            </div>
          )}
        </>
      )}
    </AppShell>
  );
};

export default Reels;
